import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { Lines } from "../components/Reveal";
import { useSiteContent } from "../lib/siteContent";
import { useStore } from "../context/StoreContext";

const DEFAULT_COLLECTIONS = [
    { name: "Drop 001", img: "/assets/COLLECTION_DROP001.jpg", meta: "The Drop" },
    { name: "Snake Edition", img: "/assets/COLLECTION_SNAKE.jpg", meta: "Transformation" },
    { name: "Raised Reality", img: "/assets/COLLECTION_RAISED.jpg", meta: "Dimensional Print" },
    { name: "Essentials", img: "/assets/COLLECTION_ESSENTIALS.jpg", meta: "The Foundation" },
];

export function useCollections() {
    const { collections } = useSiteContent();
    return collections.length ? collections : DEFAULT_COLLECTIONS;
}

function Card({ c, i, count }) {
    const meta = count != null ? `${count} ${count === 1 ? "Piece" : "Pieces"}` : c.meta;
    return (
        <Link
            data-testid={`collection-card-${c.name.toLowerCase().replace(/\s+/g, "-")}`}
            data-cursor="EXPLORE"
            to={`/shop?collection=${encodeURIComponent(c.name)}`}
            className="group relative block w-[78vw] flex-shrink-0 overflow-hidden bg-[#ECEAE4] transition-transform duration-700 ease-[cubic-bezier(0.16,1,0.3,1)] hover:[transform:perspective(1000px)_rotateY(-4deg)_rotateX(2deg)_translateY(-6px)] sm:w-[52vw] lg:w-[38vw]"
        >
            <div className="aspect-[3/4] overflow-hidden">
                <img
                    src={c.img}
                    alt={`${c.name} collection`}
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-[1400ms] ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-[1.05]"
                />
            </div>
            <div className="absolute inset-0 bg-black/25 transition-colors duration-700 group-hover:bg-black/5" />
            <div className="absolute bottom-0 left-0 right-0 flex items-end justify-between p-6">
                <div>
                    <p className="mb-2 text-[8px] uppercase tracking-[0.35em] text-[rgba(242,240,234,0.6)]">
                        {String(i + 1).padStart(2, "0")} — {meta}
                    </p>
                    <p className="font-display text-xl font-extrabold uppercase tracking-tight text-[#F2F0EA]">{c.name}</p>
                </div>
                <span className="link-underline mb-1 text-[9px] uppercase tracking-[0.3em] text-[rgba(242,240,234,0.7)]">View</span>
            </div>
        </Link>
    );
}

export default function CollectionsScroll() {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref });
    const x = useTransform(scrollYProgress, [0, 1], ["2%", "-58%"]);
    const collections = useCollections();
    const { products } = useStore();
    const countOf = (name) => {
        const n = products.filter((p) => p.collection === name).length;
        return n || null;
    };

    return (
        <section id="collections" data-testid="collections-section" className="bg-[#F2F0EA]">
            <div className="hidden md:block">
                <div ref={ref} className="relative h-[320vh]">
                    <div className="sticky top-0 flex h-screen flex-col justify-center overflow-hidden">
                        <div className="mb-10 px-16">
                            <p className="mb-3 text-[9px] uppercase tracking-[0.4em] text-[#6B6A66]">04 — Collections</p>
                            <Lines
                                lines={["The Collections"]}
                                className="font-display text-4xl font-extrabold uppercase tracking-tight text-[#0A0A0A] lg:text-6xl"
                            />
                        </div>
                        <motion.div style={{ x }} className="flex gap-6 pl-16">
                            {collections.map((c, i) => (
                                <Card key={c.name} c={c} i={i} count={countOf(c.name)} />
                            ))}
                        </motion.div>
                    </div>
                </div>
            </div>
            <div className="px-5 py-20 md:hidden">
                <p className="mb-3 text-[9px] uppercase tracking-[0.4em] text-[#6B6A66]">04 — Collections</p>
                <Lines
                    lines={["The Collections"]}
                    className="font-display mb-10 text-3xl font-extrabold uppercase tracking-tight text-[#0A0A0A]"
                />
                <div className="flex snap-x snap-mandatory gap-4 overflow-x-auto pb-4">
                    {collections.map((c, i) => (
                        <div key={c.name} className="snap-center">
                            <Card c={c} i={i} count={countOf(c.name)} />
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}
